import { observer } from "mobx-react";
import {
  ExerciseProgramModel,
  ExerciseRowModel,
  OptionExerciseForm,
  ReceiverChoice,
} from "./optionExerciseDomain";
import { Box, VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { InfoButton } from "@/components-v2/InfoButton";
import { Checkbox } from "@/components-v2/Checkbox";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { Table } from "@/components-v2/Table";
import { MTextInput } from "@/components-v2/TextInput";
import { MDateInput } from "@/components-v2/DateInput";
import { Select } from "@/components-v2/Select";
import { ShareType, shareTypeName } from "@/common/share";
import { useTranslation } from "@/stores/RootStoreContext";

export const OptionExerciseView = observer(function OptionExerciseView({
  form,
}: {
  form: OptionExerciseForm;
}) {
  const { t } = useTranslation();

  if (form.candidates.length === 0 && form.programs.length === 0) {
    return <Alert type={"info"} message={t("decisions.optionExercise.no-candidates")} />;
  }

  return (
    <VBox gap={"lg"}>
      <div>{t("decisionType.optionExercise.text")}</div>
      <Box>
        <MDateInput
          label={t("options.exercise-date")}
          model={form.exerciseDate}
          disabled={form.isProjectLocked}
        />
      </Box>
      {form.programs.map((program) => (
        <ProgramSection key={program.planId} form={form} program={program} />
      ))}
      {form.availablePrograms.length > 0 && (
        <Box>
          <DropdownMenu
            button={{
              type: "link",
              label: t("decisions.optionExercise.add-program"),
              icon: "plus",
              disabled: form.isProjectLocked,
            }}
            items={form.availablePrograms.map((choice) => ({
              type: "item",
              label: choice.programName,
              onSelect: () => form.addProgram(choice.planId),
            }))}
          />
        </Box>
      )}
      {form.error && <Alert type={"error"} message={t(form.error)} />}
      {form.capitalIncreaseDecision ? (
        <Alert
          type={"info"}
          message={
            <VBox gap={"xs"} alignItems={"start"}>
              <span>{t("decisions.optionExercise.capital-increase-info")}</span>
              <LinkButton
                label={t("decisions.optionExercise.open-capital-increase")}
                onClick={form.openCapitalIncrease}
              />
            </VBox>
          }
        />
      ) : (
        <Alert type={"warning"} message={t("decisions.optionExercise.capital-increase-missing")} />
      )}
    </VBox>
  );
});

const ProgramSection = observer(function ProgramSection({
  form,
  program,
}: {
  form: OptionExerciseForm;
  program: ExerciseProgramModel;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"sm"} border rounded padding={"md"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <strong>{program.programName}</strong>
        {!form.isProjectLocked && (
          <IconButton
            icon={"trash"}
            tooltip={t("decisions.optionExercise.remove-program")}
            onClick={() => form.removeProgram(program.planId)}
          />
        )}
      </Box>
      <Table variant={"EDITABLE_DENSE"}>
        <thead>
          <tr>
            <th align={"left"}>{t("instrument.holder")}</th>
            <th align={"right"}>{t("instrument.strike-price")}</th>
            <th align={"right"}>{t("options.exercisable")}</th>
            <th align={"right"}>{t("decisions.optionExercise.count")}</th>
            <th align={"center"}>
              <Box alignItems={"center"} justifyContent={"center"} gap={"xs"}>
                <span>{t("options.exercise-employee")}</span>
                <InfoButton infoText={{ key: "options.exercise-employee-help" }} />
              </Box>
            </th>
          </tr>
        </thead>
        <tbody>
          {program.rows.map((row) => (
            <ExerciseRow key={row.candidate.trancheId} form={form} row={row} />
          ))}
        </tbody>
      </Table>
    </VBox>
  );
});

const ExerciseRow = observer(function ExerciseRow({
  form,
  row,
}: {
  form: OptionExerciseForm;
  row: ExerciseRowModel;
}) {
  const { t, f } = useTranslation();
  const { candidate } = row;
  const isLocked = form.isProjectLocked;
  const receiverChoices = form.receiverChoices(row);

  return (
    <tr>
      <td>
        <VBox gap={"xs"}>
          <Checkbox
            checked={row.isSelected}
            disabled={isLocked || row.blocker !== undefined}
            tooltip={row.blocker}
            onChange={row.toggle}
            label={
              <VBox gap={0}>
                <span>{candidate.holderName}</span>
                {candidate.trancheName && (
                  <small className={"subtle"}>{candidate.trancheName}</small>
                )}
              </VBox>
            }
          />
          {row.isSelected && receiverChoices.length > 1 && (
            <Select<ReceiverChoice>
              compact
              label={t("options.exercise-receiver")}
              value={receiverChoices.find(
                (choice) => choice.organisationNumber === row.receiver?.organisationNumber,
              )}
              items={receiverChoices}
              idFn={(choice) => choice.organisationNumber ?? "holder"}
              labelFn={(choice) => choice.name}
              captionFn={(choice) =>
                choice.organisationNumber && { type: "org-no", value: choice.organisationNumber }
              }
              onSelect={(choice) => row.setReceiver(choice.receiver)}
              disabled={isLocked}
            />
          )}
          {row.isSelected && row.needsShareClass && (
            <Select<ShareType>
              compact
              value={form.shareTypes.find(
                (shareType) => shareType.sharetypeId === row.shareType?.shareTypeId,
              )}
              items={form.shareTypes}
              placeholder={t("options.exercise-select-share-class")}
              idFn={(shareType) => String(shareType.sharetypeId)}
              labelFn={(shareType) => shareTypeName(shareType)}
              onSelect={row.setShareType}
              error={row.isShareTypeMissing}
              message={
                row.isShareTypeMissing
                  ? t("decisions.optionExercise.share-class-required")
                  : undefined
              }
              disabled={isLocked}
            />
          )}
        </VBox>
      </td>
      <td align={"right"}>{f.formatNumber(candidate.strikePrice)}</td>
      <td align={"right"}>{f.formatNumber(row.exercisable)}</td>
      <td align={"right"}>
        {row.isSelected && (
          <MTextInput
            model={row.count}
            variant={"in-table"}
            align={"right"}
            messageVariant={"tooltip"}
            disabled={isLocked}
          />
        )}
      </td>
      <td align={"center"}>
        {row.isSelected && !candidate.isCompany && (
          <Box justifyContent={"center"}>
            <Checkbox
              checked={form.isEmployee(candidate.holderId)}
              disabled={isLocked}
              onChange={(event) => form.setEmployee(candidate.holderId, event.target.checked)}
            />
          </Box>
        )}
      </td>
    </tr>
  );
});
